import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/atoms/Button";

interface Props {
  open: boolean;
  /** What the branch change would discard, e.g. ["template", "labels"]. */
  affected: string[];
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Asks before a branch change wipes the picks that belong to the old branch.
 * The template, labels and property are all scoped to the entity/branch, so
 * switching branches has to drop them — worth a confirmation once the user has
 * actually chosen some, rather than silently emptying the fields.
 */
export function CampaignScopeChangeModal({ open, affected, onCancel, onConfirm }: Props) {
  if (!open) return null;

  const list =
    affected.length === 1
      ? affected[0]
      : `${affected.slice(0, -1).join(", ")} and ${affected[affected.length - 1]}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/45"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="campaign-scope-change-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden ring-1 ring-amber-500/10"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-amber-500/20 bg-amber-50">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-amber-100 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 id="campaign-scope-change-title" className="text-base font-bold text-slate-900">
              Change branch?
            </h2>
            <p className="text-xs text-amber-700 mt-0.5">
              This will clear the selected {list}.
            </p>
          </div>
        </div>

        <div className="px-5 py-4 text-sm text-bridge-muted">
          Templates, labels and properties belong to the entity and branch they were picked
          under, so they cannot carry over. You will need to choose them again for the new
          branch.
        </div>

        <div className="px-5 py-4 border-t border-bridge-border/60 bg-slate-50 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Keep current branch
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Change branch
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
